import React, { useState, useContext, useCallback, useRef, useEffect } from 'react'
import { useBggNumericFiltersPipeline } from '../../hooks/useBggNumericFiltersPipeline'
import { useBggOwnedCollectionFetch } from '../../hooks/useBggOwnedCollectionFetch'
import BggNumericFilters from '../BggNumericFilters'
import BggCollectionFiltersAccordion from '../BggCollectionFiltersAccordion'
import { buildOwnedCollectionParams, parseCommaSeparatedUsernames } from '../../bggCollectionQuery'
import {
	Box,
	Heading,
	Text,
	Input,
	Button,
	Checkbox,
	useToast,
	VStack,
	FormControl,
	FormLabel,
	SimpleGrid,
	Divider,
	useColorModeValue,
	Icon,
	Flex,
} from '@chakra-ui/react'
import { FaInbox } from 'react-icons/fa'

import { CollectionContext } from '../CollectionContext'
import RatingSelect from '../RatingSelect'
import { formatName, filterPlayerCount } from '../../Utils'
import { gameObjectId } from '../../ranking/rankingState'
import { clearThingMetaCache } from '../../bggThingMeta'
import GameTilePreview from '../../ranking/GameTilePreview'

const COLLECTION_PREVIEW_INITIAL = 60

function gameToPreviewItem(game) {
	const nameNode = game.name
	const name =
		typeof nameNode === 'object' && nameNode != null && nameNode['#text'] != null
			? String(nameNode['#text'])
			: String(nameNode ?? '')
	return { name, thumbnail: game.thumbnail, image: game.image }
}

export default function Step1(props) {
	const { canMoveOn, canProceedToRanking, onProceedToRanking } = props
	const { collection, setCollection } = useContext(CollectionContext)

	const [username, setUsername] = useState('')
	const { startFetch, loading } = useBggOwnedCollectionFetch({ maxRetry: 5 })

	const [minRating, setMinRating] = useState(null)
	const [rating, setRating] = useState(null)
	const [minBGGRating, setMinBGGRating] = useState(null)
	const [numPlayers, setNumPlayers] = useState(null)
	const [minPlayerCount, setMinPlayerCount] = useState(null)
	const [maxPlayerCount, setMaxPlayerCount] = useState(null)
	const [rated, setRated] = useState(false)
	const [played, setPlayed] = useState(false)
	const [comment, setComment] = useState(false)
	const [hideExpansions, setHideExpansions] = useState(false)
	const [maxPlayTime, setMaxPlayTime] = useState('any')
	const [showFullCollectionPreview, setShowFullCollectionPreview] = useState(false)

	const toast = useToast()
	const pendingFetchOutcomeRef = useRef(false)

	const panelBg = useColorModeValue('gray.50', 'bgg.900')
	const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300')
	const stripCardBg = useColorModeValue('white', 'bgg.800')
	const bodyMuted = useColorModeValue('gray.600', 'bgg.muted')
	const accordionTitleMuted = useColorModeValue('gray.600', 'bgg.muted')
	const stripMuted = useColorModeValue('gray.600', 'gray.400')

	useEffect(() => {
		setShowFullCollectionPreview(false)
	}, [collection])

	const onFilteredList = useCallback(
		(list) => {
			setCollection(list)
			if (pendingFetchOutcomeRef.current) {
				pendingFetchOutcomeRef.current = false
				toast.closeAll()
				if (list.length > 0) {
					canMoveOn(true)
					toast({
						title: 'Collection loaded',
						description:
							list.length === 1
								? '1 game loaded to the preview below. Continue by creating your ranking.'
								: `${list.length} games loaded to the preview below. Continue by creating your ranking.`,
						status: 'success',
						duration: 3000,
						isClosable: true,
					})
				} else {
					canMoveOn(false)
					toast({
						title: 'No games matched',
						description: 'Nothing left after the max playing time filter. Widen the limit or relax other filters.',
						status: 'warning',
						duration: 6000,
						isClosable: true,
					})
				}
			} else {
				canMoveOn(list.length > 0)
			}
		},
		[toast, canMoveOn, setCollection]
	)

	const { commitPlayerFiltered, resetPipeline, applyingExtraFilters } = useBggNumericFiltersPipeline(maxPlayTime, onFilteredList)

	const filtersBusy = loading || applyingExtraFilters

	const requestCollection = useCallback(() => {
		startFetch({
			getRequestConfig: () => {
				const usernames = parseCommaSeparatedUsernames(username)
				if (!usernames.length) return null
				return {
					usernames,
					params: buildOwnedCollectionParams({
						minRating,
						rating,
						minBGGRating,
						rated,
						played,
						comment,
						hideExpansions,
					}),
				}
			},
			onBeforeAttempt: () => {
				resetPipeline()
				clearThingMetaCache()
			},
			onMissingToken: () => {
				toast({
					title: 'BGG API token missing',
					description:
						'Create a token at https://boardgamegeek.com/using_the_xml_api and set REACT_APP_BGG_API_TOKEN in .env, then restart the dev server.',
					status: 'warning',
					duration: 12000,
					isClosable: true,
				})
			},
			onSuccess: (multiCollection) => {
				const playerFiltered = filterPlayerCount(multiCollection, numPlayers, minPlayerCount, maxPlayerCount)

				if (multiCollection.length === 0) {
					resetPipeline()
					setCollection([])
					toast.closeAll()
					toast({
						title: 'No games matched',
						description: 'Try another username or relax filters.',
						status: 'warning',
						duration: 5000,
						isClosable: true,
					})
					canMoveOn(false)
					return
				}

				if (playerFiltered.length === 0) {
					resetPipeline()
					setCollection([])
					toast.closeAll()
					toast({
						title: 'No games matched',
						description: 'BGG returned games, but none match your player-count filters. Try widening min/max players.',
						status: 'warning',
						duration: 6000,
						isClosable: true,
					})
					canMoveOn(false)
					return
				}

				pendingFetchOutcomeRef.current = true
				commitPlayerFiltered(playerFiltered)
			},
			onHardError: () => {
				resetPipeline()
				canMoveOn(false)
				toast({
					title: 'Could not load collection',
					description: 'Check the username or try again in a moment (BGG can be slow for large collections).',
					status: 'warning',
					duration: 9000,
					isClosable: true,
				})
			},
		})
	}, [
		startFetch,
		username,
		minRating,
		rating,
		minBGGRating,
		rated,
		played,
		comment,
		hideExpansions,
		numPlayers,
		minPlayerCount,
		maxPlayerCount,
		resetPipeline,
		commitPlayerFiltered,
		setCollection,
		canMoveOn,
		toast,
	])

	const safeCollection = collection ?? []
	const previewGames = showFullCollectionPreview ? safeCollection : safeCollection.slice(0, COLLECTION_PREVIEW_INITIAL)

	return (
		<VStack spacing={6} align="stretch">
			<Box>
				<Heading as="h3" size="md" mb={2}>
					Load your collection
				</Heading>
				<Text color={bodyMuted} fontSize="sm" lineHeight="tall">
					Load your BGG collection, confirm by button and create your own tier list.
				</Text>
			</Box>

			<VStack spacing={2} align="stretch">
				<FormControl>
					<FormLabel fontSize="sm" fontWeight="600">
						BGG username
					</FormLabel>
					<Input size="lg" rounded="lg" placeholder="e.g. vitek38" value={username} onChange={(e) => setUsername(e.target.value)} />
				</FormControl>

				<BggCollectionFiltersAccordion>
					<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
						<RatingSelect label="Min. your rating" value={minRating} onChange={(e) => setMinRating(e.target.value)} />
						<RatingSelect label="Max. your rating" value={rating} onChange={(e) => setRating(e.target.value)} />
						<RatingSelect label="Min. BGG rating" value={minBGGRating} onChange={(e) => setMinBGGRating(e.target.value)} />
						<RatingSelect label="Players (exact)" value={numPlayers} onChange={(e) => setNumPlayers(e.target.value)} />
						<RatingSelect label="Min. players" value={minPlayerCount} onChange={(e) => setMinPlayerCount(e.target.value)} />
						<RatingSelect label="Max. players" value={maxPlayerCount} onChange={(e) => setMaxPlayerCount(e.target.value)} />
					</SimpleGrid>
					<Divider my={5} />
					<BggNumericFilters
						maxPlayTime={maxPlayTime}
						onMaxPlayTimeChange={(e) => setMaxPlayTime(e.target.value)}
						disabled={filtersBusy}
					/>
					<Divider my={5} />
					<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
						<Checkbox isChecked={hideExpansions} onChange={() => setHideExpansions(!hideExpansions)} size="md">
							Hide expansions
						</Checkbox>
						<Checkbox isChecked={rated} onChange={() => setRated(!rated)} size="md">
							Rated on BGG
						</Checkbox>
						<Checkbox isChecked={played} onChange={() => setPlayed(!played)} size="md">
							Marked played
						</Checkbox>
						<Checkbox isChecked={comment} onChange={() => setComment(!comment)} size="md">
							Has comment
						</Checkbox>
					</SimpleGrid>
				</BggCollectionFiltersAccordion>
			</VStack>

			<Box w="100%">
				<Button
					colorScheme="brand"
					variant="solid"
					isLoading={filtersBusy}
					onClick={requestCollection}
					isDisabled={username.trim().length === 0 || applyingExtraFilters}
					size="lg"
					rounded="lg"
					w="100%"
					aria-busy={filtersBusy}
				>
					Fetch collection
				</Button>
			</Box>

			<Box borderWidth="1px" borderColor={cardBorder} rounded="lg" bg={panelBg} overflow="hidden">
				<Box px={4} py={3} borderBottomWidth={safeCollection.length > 0 ? '1px' : '0'} borderColor={cardBorder}>
					<Text fontWeight="600" fontSize="sm" color={accordionTitleMuted}>
						{safeCollection.length === 0
							? 'Collection preview'
							: `${formatName(username)} · ${safeCollection.length} games`}
					</Text>
				</Box>
				{safeCollection.length > 0 ? (
					<Box px={0} pt={4} pb={4}>
						<Flex
							overflowX="auto"
							overflowY="hidden"
							flexWrap="nowrap"
							alignItems="stretch"
							gap={2}
							px={4}
							pb={2}
							width="100%"
							maxW="100%"
							sx={{ WebkitOverflowScrolling: 'touch' }}
						>
							{previewGames.map((game) => (
								<Box key={gameObjectId(game) || game.name['#text']} flexShrink={0}>
									<GameTilePreview item={gameToPreviewItem(game)} />
								</Box>
							))}
						</Flex>
						{safeCollection.length > COLLECTION_PREVIEW_INITIAL && !showFullCollectionPreview ? (
							<Box px={4}>
								<Button
									variant="link"
									size="sm"
									colorScheme="brand"
									onClick={() => setShowFullCollectionPreview(true)}
									mt={1}
								>
									Show all {safeCollection.length} games (long lists can feel slow)
								</Button>
							</Box>
						) : null}
						<Box px={4} pt={4}>
							<Button
								colorScheme="brand"
								size="lg"
								rounded="lg"
								onClick={onProceedToRanking}
								isDisabled={!canProceedToRanking}
							>
								Create ranking
							</Button>
						</Box>
					</Box>
				) : (
					<VStack
						spacing={3}
						py={6}
						px={4}
						align="center"
						textAlign="center"
						borderTopWidth="0"
						borderColor={cardBorder}
						bg={stripCardBg}
					>
						<Icon as={FaInbox} boxSize="36px" color={stripMuted} aria-hidden />
						<Text fontSize="sm" fontWeight="600" color={accordionTitleMuted}>
							No collection loaded yet
						</Text>
						<Text fontSize="sm" color={stripMuted} maxW="md">
							Enter your BoardGameGeek username and choose Fetch collection. After BGG responds, your filtered games will appear
							here.
						</Text>
					</VStack>
				)}
			</Box>
		</VStack>
	)
}
